import { config, george, pr, theme, toJS, ui } from "aaa";
import { NewEmail } from "components/common/NewEmail";
import { RadioButtonGroup } from "components/common/RadioButtonGroup";
import { observer } from "mobx-react";
import { themeColors } from "muiTheme";
import * as React from "react";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { SurveyRoutes } from "./SurveyRoutes";
import { emptyOption, ISurveyOption, SurveyStore } from "./SurveyStore";

const alignmentToCss = (alignment: "LEFT" | "CENTER" | "RIGHT" | undefined): "left" | "center" | "right" | undefined => {
    switch (alignment) {
        case "LEFT":
            return "left";
        case "CENTER":
            return "center";
        case "RIGHT":
            return "right";
        default:
            return alignment;
    }
};

export const Survey = observer(({ store }: { store: SurveyStore }) => {
    const [showNewEmail, setShowNewEmail] = React.useState(false);

    const formDefinition = store.formDefinition;

    const PageComponent = externalInterfaceStore.isGeorgeApp ? george.PageWithActionButton : React.Fragment;

    const handleSubmit = async () => {
        try {
            await store.submitSurvey();
            pushRoute(SurveyRoutes.SIGNING);
        } catch (error) {
            if (!config.DEBUG) {
                pushRoute("/error");
            }
        }
    };

    if (store.isLoading()) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    return (
        <george.Background>
            <PageComponent>
                {formDefinition?.body.map((element, index) => {
                    const key = `${element.component}_${index}`;

                    switch (element.component) {
                        case "headline": {
                            const isFirstElement = index === 0;
                            const TitleComponent = isFirstElement ? pr.P_24_TITLE : pr.P_20_CONTENT;

                            return (
                                <pr.A11yPopupHider key={key}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            height: isFirstElement ? 100 : undefined,
                                            boxSizing: "border-box",
                                            backgroundColor: isFirstElement ? themeColors.blue_300 : "rgba(25, 156, 249, 0.2)",
                                            padding: theme.MARGIN,
                                            marginTop: index > 0 ? 10 : undefined,
                                            ...isFirstElement ? {} : pr.STYLE_CENTERED_CARD
                                        }}
                                    >
                                        <TitleComponent
                                            style={{
                                                color: isFirstElement || ui.isDarkMode ? "white" : undefined,
                                                fontWeight: 700,
                                                textAlign: alignmentToCss(element.titleAlignment)
                                            }}
                                        >
                                            {element.title}
                                        </TitleComponent>
                                    </div>
                                </pr.A11yPopupHider>
                            );
                        }
                        case "card": {
                            return (
                                <pr.A11yPopupHider key={key}>
                                    <george.Card>
                                        {element.title && (
                                            <george.CardSectionTitle
                                                title={element.title}
                                                style={{ textAlign: alignmentToCss(element.titleAlignment) }}
                                            />
                                        )}
                                        <george.CardSectionContent style={{ paddingTop: element.title ? undefined : theme.MARGIN }}>
                                            <pr.P_16_CONTENT
                                                dangerouslySetInnerHTML={{ __html: element.text }}
                                                style={{ textAlign: alignmentToCss(element.textAlignment) }}
                                            />
                                        </george.CardSectionContent>
                                    </george.Card>
                                </pr.A11yPopupHider>
                            );
                        }
                        case "image": {
                            return (
                                <pr.A11yPopupHider key={key}>
                                    {element.title && (
                                        <pr.P_16_TITLE style={{ fontWeight: 600, textAlign: alignmentToCss(element.titleAlignment) }}>
                                            {element.title}
                                        </pr.P_16_TITLE>
                                    )}
                                    <div style={{ marginTop: 10, textAlign: alignmentToCss(element.imageAlignment) }}>
                                        <img
                                            src={element.image?.path}
                                            style={{ maxWidth: "100%", width: element.image?.width }}
                                        />
                                    </div>
                                </pr.A11yPopupHider>
                            );
                        }
                        case "form": {
                            return (
                                <george.Card key={key}>
                                    <george.CardSectionContent style={{ display: "flex", flexDirection: "column", gap: theme.MARGIN, paddingTop: theme.MARGIN }}>
                                        {element.items?.map((formElement, formElementIndex) => {
                                            const formElementKey = `${formElement.component}_${formElementIndex}`;
                                            const formElementState = toJS(store.formState[formElement.id]);

                                            const labelComponent = (
                                                <pr.P_16_TITLE style={{ fontWeight: 600, marginBottom: theme.MARGIN_SMALL }}>
                                                    {formElement.label}
                                                </pr.P_16_TITLE>
                                            );

                                            switch (formElement.component) {
                                                case "textInput": {
                                                    return typeof formElementState === "string" ? (
                                                        <pr.A11yPopupHider key={formElementKey}>
                                                            <div>
                                                                {labelComponent}
                                                                <george.TextField
                                                                    id={formElement.id}
                                                                    multiLine
                                                                    rows={3}
                                                                    onChange={(event, value) => {
                                                                        store.formState = {
                                                                            ...store.formState,
                                                                            [formElement.id]: value
                                                                        };
                                                                    }}
                                                                    value={formElementState ?? ""}
                                                                    aria-label={formElement.label}
                                                                    hintText={formElement.placeholder ?? undefined}
                                                                    inputStyle={{ color: ui.isDarkMode ? "white" : "#14141A" }}
                                                                />
                                                            </div>
                                                        </pr.A11yPopupHider>
                                                    ) : null;
                                                }
                                                case "selection": {
                                                    let options = formElement.options ?? [];

                                                    if (formElement.productType === "EMAIL") {
                                                        options = store.getEmailOptions(formElement);
                                                    }

                                                    if (formElement.productType === "PHONE") {
                                                        options = store.getPhoneOptions(formElement);
                                                    }

                                                    if (formElement.productType === "ADDRESS") {
                                                        options = store.getAddressOptions(formElement);
                                                    }

                                                    if (formElement.product === "GIRO" || formElement.product === "SAVINGS") {
                                                        options = store.getAccountOptions(formElement);
                                                    }

                                                    if (formElement.product === "BUILDING_SAVINGS") {
                                                        options = store.getBuildingsAccountOptions(formElement);
                                                    }

                                                    if (
                                                        formElement.product === "BRAND_INDEPENDENT" ||
                                                        formElement.product === "MASTERCARD" ||
                                                        formElement.product === "VISA"
                                                    ) {
                                                        options = store.getCardOptions(formElement);
                                                    }

                                                    switch (formElement.displayMode) {
                                                        case "RADIO_BUTTONS": {
                                                            return typeof formElementState === "object" && !Array.isArray(formElementState) ? (
                                                                <pr.A11yPopupHider key={formElementKey}>
                                                                    <div>
                                                                        {labelComponent}
                                                                        <RadioButtonGroup
                                                                            name={formElement.id}
                                                                            value={formElementState.value}
                                                                            options={options}
                                                                            onChange={(value, option) => {
                                                                                if (option) {
                                                                                    store.formState = {
                                                                                        ...store.formState,
                                                                                        [formElement.id]: option
                                                                                    };
                                                                                }
                                                                            }}
                                                                            radioButtonStyle={{ marginLeft: 0, marginRight: 0 }}
                                                                        />
                                                                    </div>
                                                                </pr.A11yPopupHider>
                                                            ) : null;
                                                        }
                                                        case "CHECKBOXES": {
                                                            return Array.isArray(formElementState) ? (
                                                                <pr.A11yPopupHider key={formElementKey}>
                                                                    <div>
                                                                        {labelComponent}
                                                                        <div style={{ display: "flex", flexDirection: "column", gap: theme.MARGIN, marginTop: theme.MARGIN }}>
                                                                            {options.map(option => (
                                                                                <george.Checkbox
                                                                                    key={option.value}
                                                                                    label={option.label}
                                                                                    defaultChecked={!!formElementState.find(formStateOption =>
                                                                                        formStateOption.value === option.value
                                                                                    )}
                                                                                    onCheck={(event, checked) => {
                                                                                        store.formState = {
                                                                                            ...store.formState,
                                                                                            [formElement.id]: checked
                                                                                                ? [...formElementState, option]
                                                                                                : formElementState.filter((savedOption: ISurveyOption) =>
                                                                                                    savedOption.value !== option.value
                                                                                                )
                                                                                        };
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </pr.A11yPopupHider>
                                                            ) : null;
                                                        }
                                                        case "DROPDOWN": {
                                                            if (formElement.productType === "EMAIL") {
                                                                options = [...options, { value: "NEW_EMAIL", label: ui.__("email.addNew") }];
                                                            }

                                                            if (formElement.productType === "PHONE") {
                                                                options = [...options, { value: "NEW_PHONE", label: ui.__("phone.addNew") }];
                                                            }

                                                            if (formElement.productType === "ADDRESS") {
                                                                options = [...options, { value: "NEW_ADDRESS", label: ui.__("address.addNew") }];
                                                            }

                                                            options = [emptyOption, ...(options ?? [])];

                                                            return typeof formElementState === "object" && !Array.isArray(formElementState) ? (
                                                                <div key={formElementKey}>
                                                                    <pr.A11yPopupHider>
                                                                        {labelComponent}
                                                                        <george.DropDown
                                                                            value={formElementState.value}
                                                                            items={options}
                                                                            onChange={(event, selectionIndex, value) => {
                                                                                if (value === "NEW_EMAIL") {
                                                                                    setShowNewEmail(true);
                                                                                    ui.isPopupVisible = true;
                                                                                } else if (value === "NEW_PHONE") {
                                                                                    pushRoute(SurveyRoutes.NEW_PHONE);
                                                                                } else if (value === "NEW_ADDRESS") {
                                                                                    pushRoute(SurveyRoutes.NEW_ADDRESS);
                                                                                } else {
                                                                                    store.formState[formElement.id] = options[selectionIndex];
                                                                                }
                                                                            }}
                                                                        />
                                                                    </pr.A11yPopupHider>
                                                                </div>
                                                            ) : null;
                                                        }
                                                        default: return null;
                                                    }
                                                }
                                                default: return null;
                                            }
                                        })}
                                    </george.CardSectionContent>
                                </george.Card>
                            );
                        }
                        default: return null;
                    }
                })}
                <pr.A11yPopupHider>
                    <div style={pr.STYLE_CENTERED_CARD}>
                        <george.ActionButton
                            messageId="button.continue"
                            onClick={handleSubmit}
                            disableButton={!store.canSubmit}
                            {...george.getActionButtonWebVersionProps()}
                        />
                    </div>
                </pr.A11yPopupHider>
                <NewEmail
                    show={showNewEmail}
                    contactsStore={store.contactsStore}
                    onClose={
                        () => {
                            setShowNewEmail(false);
                            ui.isPopupVisible = false;
                        }
                    }
                />
            </PageComponent>
        </george.Background>
    );
});


// WEBPACK FOOTER //
// ./src/components/survey/Survey.tsx